import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Modal } from '@/components/ui';
import { AddressDetailsFields } from '@/features/address/AddressDetailsFields';
import { labelTaken, labelTakenMessage, type DeliverySubmit } from '@/features/address/model';
import type { ApiError } from '@/lib/api/client';
import type { AddressOut } from '@/lib/api/types';
import { deliveryDetailsSchema, type DeliveryDetailsValues } from '@/lib/validators';

export interface AddressEditorProps {
  open: boolean;
  onClose: () => void;
  /** The address being corrected. */
  address: AddressOut;
  onSave: (values: DeliverySubmit) => void;
  saving: boolean;
  error?: ApiError | null;
  /** Labels the customer's OTHER addresses use. This one's own is not among them. */
  takenLabels?: readonly string[];
}

/**
 * Correct one saved address.
 *
 * THE SAME FIELDS AS THE CHECKOUT, from the same component, so an address
 * edited here is exactly what the checkout would have produced. It used to be
 * a second, hand-written copy of the form; the two were already one field
 * apart when this replaced it.
 *
 * THE PIN IS NOT EDITED HERE. The area, city and PIN code were read off the
 * pin when the address was saved, and moving the pin would mean re-naming all
 * three — a different address, not a corrected one. A customer who has moved
 * house adds the new place at checkout and deletes this one.
 */
export function AddressEditor({
  open,
  onClose,
  address,
  onSave,
  saving,
  error,
  takenLabels = [],
}: AddressEditorProps) {
  const formId = useId();

  const form = useForm<DeliveryDetailsValues>({
    resolver: zodResolver(deliveryDetailsSchema),
    mode: 'onBlur',
    defaultValues: toFormValues(address),
  });
  const { reset, setError, handleSubmit, getValues } = form;

  // Re-seed whenever a different address is opened, or the same one is
  // reopened after a cancelled edit: without this the modal would still be
  // holding the previous row's values.
  useEffect(() => {
    if (open) reset(toFormValues(address));
  }, [open, address, reset]);

  // The server's 422 is the authority on fields the client cannot check.
  useEffect(() => {
    if (!error) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      if (field in getValues()) {
        setError(field as keyof DeliveryDetailsValues, { type: 'server', message });
      }
    }
  }, [error, setError, getValues]);

  const submit = handleSubmit((values) => {
    const label = values.label?.trim() ?? '';
    if (labelTaken(label, takenLabels)) {
      setError('label', { type: 'taken', message: labelTakenMessage(label) });
      return;
    }
    onSave({
      name: values.name,
      phone: values.phone,
      line1: values.line1,
      landmark: values.landmark?.trim() || null,
      label: values.label?.trim() || null,
      // Read off the pin when it was saved; not re-asked, not re-derived.
      locality: address.locality ?? null,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
    });
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit address"
      description={[address.locality, `${address.city} ${address.pincode}`]
        .filter(Boolean)
        .join(', ')}
      variant="sheet"
      footer={
        <>
          <Button type="submit" form={formId} loading={saving} fullWidth>
            Save changes
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saving} fullWidth>
            Cancel
          </Button>
        </>
      }
    >
      {/* Only the messages the fields cannot carry themselves. A 422 with field
          errors is already shown against the offending input. */}
      {error && Object.keys(error.fieldErrors).length === 0 ? (
        <Alert severity="danger" title="We could not save that">
          <p>{error.message}</p>
        </Alert>
      ) : null}

      <form
        id={formId}
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <AddressDetailsFields
          form={form}
          needsArea={false}
          needsPincode={false}
          disabled={saving}
          takenLabels={takenLabels}
        />
      </form>
    </Modal>
  );
}

/**
 * The API models "none" as null; the form models it as an empty string.
 * Passing null into an input makes it uncontrolled and React complains at the
 * moment the customer first types.
 */
function toFormValues(address: AddressOut): DeliveryDetailsValues {
  return {
    line1: address.line1,
    // An address saved when area and landmark were separate boxes lands in
    // the landmark line either way: it is the only free-text line left and
    // both were the same kind of hint. Saving writes it there and clears
    // line2 (see SavedAddresses), or the text prints twice on the label.
    landmark: [address.line2, address.landmark].filter(Boolean).join(', '),
    label: address.label ?? '',
    name: address.name,
    phone: address.phone.replace(/^\+91/, ''),
    city: address.city,
    pincode: address.pincode,
  };
}
